import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/lib/auth-store";
import { getAccessToken, baseURL } from "@/lib/api";

/**
 * Subscribes to the backend's notifications SSE stream while the user is
 * authenticated. On every event we invalidate the relevant query keys so
 * the bell badge / list re-fetch automatically (no full reload).
 *
 * Resilience:
 * - Exponential backoff (1s → 30s) on connection error.
 * - Pauses while the tab is hidden (resumes on visibility).
 * - Skips when the user is offline (resumes on `online`).
 * - Closes cleanly on logout / unmount.
 */
export function useNotificationsStream() {
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? null);

  useEffect(() => {
    if (!userId) return;

    let es: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    let backoffMs = 1000;
    const BACKOFF_MAX = 30_000;

    const clearReconnect = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const close = () => {
      clearReconnect();
      if (es) {
        es.close();
        es = null;
      }
    };

    const invalidateAll = () => {
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
      qc.invalidateQueries({ queryKey: ["notif-recent"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    };

    const handleTypedEvent = (ev: MessageEvent) => {
      invalidateAll();
      try {
        const data = JSON.parse(ev.data || "{}");
        const cat = (data.category || data.type || "").toString().toLowerCase();
        if (cat.includes("leave")) qc.invalidateQueries({ queryKey: ["leaves"] });
        if (cat.includes("sick")) qc.invalidateQueries({ queryKey: ["sick-leaves"] });
        if (cat.includes("shift") || cat.includes("clock")) {
          qc.invalidateQueries({ queryKey: ["shifts"] });
          qc.invalidateQueries({ queryKey: ["my-timeline"] });
        }
        if (cat.includes("task")) qc.invalidateQueries({ queryKey: ["tasks"] });
      } catch {
        /* ignore malformed payloads */
      }
    };

    const open = () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.hidden) return;
      if (typeof navigator !== "undefined" && !navigator.onLine) return;

      const token = getAccessToken();
      if (!token) {
        reconnectTimer = setTimeout(open, 500);
        return;
      }

      const url = `${baseURL}/api/web/notifications/stream?token=${encodeURIComponent(token)}`;
      es = new EventSource(url, { withCredentials: true });

      es.addEventListener("notification", handleTypedEvent as EventListener);
      es.addEventListener("message", handleTypedEvent as EventListener);

      es.onopen = () => {
        backoffMs = 1000;
      };

      es.onerror = () => {
        close();
        if (cancelled) return;
        reconnectTimer = setTimeout(open, backoffMs);
        backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX);
      };
    };

    const onVisibility = () => {
      if (document.hidden) {
        close();
      } else if (!es) {
        backoffMs = 1000;
        open();
      }
    };

    const onOnline = () => {
      if (!es && !document.hidden) {
        backoffMs = 1000;
        open();
      }
    };

    const onOffline = () => close();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    open();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      close();
    };
  }, [userId, qc]);
}
